import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import {
  LayoutGrid, List, Disc3, Eye, Heart, MessageCircle, Send, Activity, Zap,
} from "lucide-react";
import { thumbUrl } from "@/lib/api";
import { PlatformBadge } from "@/components/PlatformBadge";
import { StatCard } from "@/components/StatCard";
import { PostCard } from "@/components/PostCard";
import { PromoteSourceCard } from "@/components/PromoteSourceCard";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { fmtFull, fmtShort, fmtDate } from "@/lib/format";

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-xl border border-black/10 bg-white px-3 py-2 shadow-lg">
      <p className="mb-1 text-xs font-medium text-neutral-500">{fmtDate(label)}</p>
      {payload.map((p) => (
        <p key={p.dataKey} className="num-display text-sm font-semibold" style={{ color: p.color }}>
          {p.name}: {fmtFull(p.value)}
        </p>
      ))}
    </div>
  );
}

export function CampaignReport({ data, hideHeader = false, middleSlot = null, editable = false, artistImage = "", dashZeros = false }) {
  const [sort, setSort] = useState("views");
  const [view, setView] = useState("grid");
  const [visible, setVisible] = useState(50);

  const camp = data.campaign;
  const summary = data.summary;
  const posts = data.posts || [];
  const snapshots = data.snapshots || [];
  // Post cards never show a "cover unavailable" tile — fall back to the artist photo (or the
  // campaign cover, which is the artist photo for cleaned imports), else the Playbook logo.
  const postFallbackImage = artistImage || data?.artist?.image || data?.campaign?.cover_art || "";
  const dash = (v, f = (x) => x) => (v === null || v === undefined ? "—" : f(v));
  // Promote.fun campaigns are a source snapshot (no creator posts). Hide the post-performance
  // block entirely until real posts exist, so we never show "unavailable" post metrics for them.
  const showPosts = !camp?.promote || posts.length > 0;

  const sortedPosts = useMemo(() => {
    const arr = [...posts];
    if (sort === "engagement") arr.sort((a, b) => b.engagement_rate - a.engagement_rate);
    else arr.sort((a, b) => b.views - a.views);
    return arr;
  }, [posts, sort]);
  const shownPosts = sortedPosts.slice(0, visible);

  return (
    <>
      {/* Header card */}
      {!hideHeader && camp && (
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="pb-card flex flex-col gap-5 p-5 sm:flex-row sm:items-center">
        <div className="h-24 w-24 shrink-0 overflow-hidden rounded-2xl bg-neutral-900">
          {camp.cover_art ? <img src={thumbUrl(camp.cover_art, 240)} alt={camp.title} width={96} height={96} loading="lazy" decoding="async" className="h-full w-full object-cover" /> : <div className="grid h-full place-items-center text-neutral-600"><Disc3 className="h-8 w-8" /></div>}
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2"><PlatformBadge platform={camp.platform} /></div>
          <h1 className="mt-2 font-display text-2xl font-extrabold tracking-tight sm:text-3xl">
            {camp.title} <span className="text-neutral-400">— {camp.artist}</span>
          </h1>
          <p className="mt-1 text-sm text-neutral-500">Started {fmtDate(camp.start_date)}</p>
        </div>
        <div className="flex gap-3">
          <div className="rounded-2xl border border-black/[0.06] bg-neutral-50 px-5 py-3 text-center">
            <p className="num-display text-2xl font-extrabold" data-testid="live-posts-count">{summary.post_count}</p>
            <p className="text-xs text-neutral-500">Live Posts</p>
          </div>
          <div className="rounded-2xl border border-black/[0.06] bg-neutral-50 px-5 py-3 text-center">
            <p className={`text-sm font-bold ${camp.status === "complete" ? "text-[#065F46]" : "text-[#92400E]"}`}>{camp.status === "complete" ? "Complete" : "In Progress"}</p>
            <p className="text-xs text-neutral-500">Status</p>
          </div>
        </div>
      </motion.div>
      )}

      {camp?.promote && <PromoteSourceCard promote={camp.promote} />}

      {showPosts && (
      <>
      {/* Post Performance */}
      <h2 className="mb-4 mt-10 font-display text-xl font-bold">Post Performance</h2>
      <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
        <StatCard testId="stat-total-views" size="lg" label="Total Views" value={dash(summary.total_views, fmtShort)} sub={summary.total_views == null ? "Not publicly available" : `${fmtFull(summary.total_views)} lifetime views`} icon={Eye} index={0} />
        <StatCard testId="stat-avg-er" size="lg" label="Avg. Campaign Engagement Rate" value={summary.avg_engagement_rate == null ? "—" : `${summary.avg_engagement_rate}%`} sub="Across all creator posts" icon={Activity} index={1} />
        <StatCard testId="stat-total-engagement" size="lg" label="Total Engagement" value={dash(summary.total_engagement, fmtShort)} sub={`${fmtFull(summary.total_engagement || 0)} interactions`} icon={Zap} index={2} />
      </div>

      {/* Chart */}
      <div className="pb-card mt-6 p-6">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h3 className="font-display text-lg font-bold">Views / Engagement</h3>
            <p className="text-sm text-neutral-500">Cumulative growth over time</p>
          </div>
          <div className="flex gap-4 text-xs">
            <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-[#35606E]" /> Views</span>
            <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-[#E1306C]" /> Engagement</span>
          </div>
        </div>
        <div className="h-72 w-full" data-testid="views-engagement-chart">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={snapshots} margin={{ top: 5, right: 8, left: 8, bottom: 0 }}>
              <defs>
                <linearGradient id="gViews" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#35606E" stopOpacity={0.18} />
                  <stop offset="100%" stopColor="#35606E" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="gEng" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#E1306C" stopOpacity={0.18} />
                  <stop offset="100%" stopColor="#E1306C" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid vertical={false} stroke="#00000008" />
              <XAxis dataKey="date" tickFormatter={(d) => new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric" })} tick={{ fontSize: 11, fill: "#9ca3af" }} axisLine={false} tickLine={false} minTickGap={30} />
              <YAxis yAxisId="left" tickFormatter={fmtShort} tick={{ fontSize: 11, fill: "#9ca3af" }} axisLine={false} tickLine={false} width={40} />
              <YAxis yAxisId="right" orientation="right" tickFormatter={fmtShort} tick={{ fontSize: 11, fill: "#9ca3af" }} axisLine={false} tickLine={false} width={40} />
              <Tooltip content={<ChartTooltip />} />
              <Area yAxisId="left" type="monotone" dataKey="cumulative_views" name="Views" stroke="#35606E" strokeWidth={2.5} fill="url(#gViews)" />
              <Area yAxisId="right" type="monotone" dataKey="cumulative_engagement" name="Engagement" stroke="#E1306C" strokeWidth={2.5} fill="url(#gEng)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {middleSlot}

      {/* Posts */}
      <div className="mb-4 mt-10 flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
        <h2 className="font-display text-xl font-bold">Posts</h2>
        <div className="flex items-center gap-2">
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger data-testid="sort-select" className="w-40 rounded-full border-neutral-300 bg-white"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="views">Sort: Views</SelectItem>
              <SelectItem value="engagement">Sort: Engagement</SelectItem>
            </SelectContent>
          </Select>
          <div className="flex rounded-full border border-neutral-300 bg-white p-1">
            <button data-testid="view-grid" onClick={() => setView("grid")} className={`grid h-8 w-8 place-items-center rounded-full transition-colors ${view === "grid" ? "bg-[#35606E] text-white" : "text-neutral-500"}`}><LayoutGrid className="h-4 w-4" /></button>
            <button data-testid="view-list" onClick={() => setView("list")} className={`grid h-8 w-8 place-items-center rounded-full transition-colors ${view === "list" ? "bg-[#35606E] text-white" : "text-neutral-500"}`}><List className="h-4 w-4" /></button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-5">
        <StatCard testId="stat-posts" label="Total Posts" value={fmtFull(summary.post_count)} icon={LayoutGrid} index={0} />
        <StatCard testId="stat-posts-views" label="Total Views" value={dash(summary.total_views, fmtShort)} icon={Eye} index={1} />
        <StatCard testId="stat-posts-likes" label="Total Likes" value={dash(summary.total_likes, fmtShort)} icon={Heart} index={2} />
        <StatCard testId="stat-posts-comments" label="Total Comments" value={dash(summary.total_comments, fmtFull)} icon={MessageCircle} index={3} />
        <StatCard testId="stat-posts-shares" label="Total Shares" value={dash(summary.total_shares, fmtFull)} icon={Send} index={4} />
      </div>

      {posts.length === 0 ? (
        <div className="pb-card mt-6 flex flex-col items-center gap-3 p-14 text-center">
          <Disc3 className="h-10 w-10 text-neutral-300" />
          <p className="font-medium text-neutral-600">No posts yet</p>
        </div>
      ) : view === "grid" ? (
        <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {shownPosts.map((p, i) => <PostCard key={p.id} post={p} index={i} view="grid" editable={editable} artistImage={postFallbackImage} dashZeros={dashZeros} />)}
        </div>
      ) : (
        <div className="mt-6 space-y-3">
          {shownPosts.map((p, i) => <PostCard key={p.id} post={p} index={i} view="list" editable={editable} artistImage={postFallbackImage} dashZeros={dashZeros} />)}
        </div>
      )}
      {sortedPosts.length > visible && (
        <div className="mt-6 flex justify-center">
          <button
            data-testid="show-more-posts"
            onClick={() => setVisible((v) => v + 50)}
            className="rounded-full border border-neutral-300 bg-white px-6 py-2.5 text-sm font-medium text-neutral-700 transition-colors hover:bg-neutral-50"
          >
            Show more ({sortedPosts.length - visible} left)
          </button>
        </div>
      )}
      </>
      )}
    </>
  );
}
